/**
 * 单设备详情 —— 注册表里真有的那几件事,加上真能做的两个动作(轮换/吊销)。
 *
 * 「概览」里的遥测格子原来画的是 mock 的电量/温度/CPU/GPU。hub 不存这些,
 * 所以这里画的是"暂无数据源 + 第几期接",不是 0%。配置/日志/升级三个页签
 * 同理:那套基础设施(P4)还不存在,与其展示假日志,不如说清楚还没有。
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { Terminal } from '@/types/fleet';
import { hub, HubError } from '@/lib/hubClient';
import { text, timeAgo, pct, degC, NA } from '@/lib/format';
import { NoDataSource } from '@/components/feature/DataSource';

const typeLabels: Record<string, string> = { dog: '机器狗', glasses: 'AI眼镜', phone: '手机', drone: '无人机', vehicle: '无人车', other: '其它' };

interface Props { device: Terminal; onClose: () => void; onChanged: () => void; }

export default function DeviceDetailSheet({ device, onClose, onChanged }: Props) {
  const [tab, setTab] = useState<'overview' | 'access' | 'config' | 'logs' | 'upgrade'>('overview');
  const [newToken, setNewToken] = useState('');
  const [busy, setBusy] = useState<'rotate' | 'revoke' | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState<'rotate' | 'revoke' | null>(null);

  const fail = (e: unknown) => setError(
    e instanceof HubError && e.status === 403 ? '仅座舱(admin 令牌)可执行此操作'
      : e instanceof Error ? e.message : String(e));

  // ★★ 轮换成功后【不】立刻 onChanged()。
  //   明文令牌只存在于本组件的 state 里, 而 onChanged 是父页面的 refetch ——
  //   刷新期间只要有任何一层因为"正在加载"而不渲染, 这个组件就被卸载, 令牌随之
  //   蒸发, 而服务端那把旧的已经作废(2026-09-11 现场就是这样一把都没拿到)。
  //   页面那头的首屏判据已经改成 `loaded`, 但这里仍然把刷新推迟到用户确认收下
  //   令牌之后 —— 一次性凭证不该把自己的存活押在别人的渲染时序上。
  const rotate = async () => {
    setBusy('rotate'); setError('');
    try { const r = await hub.rotateDevice(device.id); setNewToken(r.token); setConfirm(null); }
    catch (e) { fail(e); } finally { setBusy(null); }
  };
  /** 用户说"收下了": 这时才去刷新列表(lastSeen/状态会变)。 */
  const tokenTaken = () => { setNewToken(''); onChanged(); };
  const revoke = async () => {
    setBusy('revoke'); setError('');
    try { await hub.revokeDevice(device.id); setConfirm(null); onChanged(); onClose(); }
    catch (e) { fail(e); } finally { setBusy(null); }
  };

  const tabs = [
    { id: 'overview' as const, label: '概览' },
    { id: 'access' as const, label: '接入' },
    { id: 'config' as const, label: '配置' },
    { id: 'logs' as const, label: '日志' },
    { id: 'upgrade' as const, label: '升级' },
  ];

  const Row = ({ k, v }: { k: string; v: string }) => (
    <div className="flex justify-between gap-3 py-1.5"><span className="text-foreground-400 shrink-0">{k}</span><span className="font-mono text-foreground-700 text-right break-all">{v}</span></div>
  );

  // ★ 必须 portal 到 body。2026-07-26 实测: 抽屉的 `fixed top-0 bottom-0` 量出来是
  //   top=148 h=322(视口 900) —— 因为祖先里那个 `animate-fade-in-up` 动画结束后
  //   仍带着 transform(matrix(1,0,0,1,0,0), 即便是单位矩阵), 而带 transform 的元素会
  //   **创建新的包含块**, 于是 position:fixed 不再相对视口, 被关进那个动画容器的盒子里。
  //   后果: 内容区只有 208px 而内容有 643px —— 详情看起来"只显示一小块"。
  //   覆盖层本来就不该受任何祖先的布局/变换影响, portal 到 body 是根治, 而不是去
  //   逐个删祖先的动画(那既治不住下一个, 也会牵动一堆页面的观感)。
  return createPortal(
    <>
      {/* 遮罩只在窄屏出现。宽屏(xl+)详情与列表【并排】——设备管理是要对照着看的页面:
          压暗列表会让人没法一边看详情一边扫其它设备, 还得先关掉详情才能点下一台。
          宽屏让出空间的那半在 DeviceList 里(xl:pr-[496px])。 */}
      <div className="fixed inset-0 z-40 bg-foreground-950/20 animate-backdrop-in xl:hidden" onClick={onClose}></div>
      <div className="fixed right-0 top-0 bottom-0 w-[480px] max-w-full bg-background-50 border-l border-background-200/70 z-50 flex flex-col animate-sheet-slide-in">
        <div className="px-5 py-4 border-b border-background-200/70 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${device.revoked ? 'bg-red-400' : device.status === 'online' ? 'bg-accent-500' : 'bg-foreground-300'}`}></span>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground-800 font-heading truncate">{device.name}</h3>
                <p className="text-xs text-foreground-500 mt-0.5 truncate">{typeLabels[device.type]} · <span className="font-mono">{device.id}</span></p>
              </div>
            </div>
            <button onClick={() => { if (newToken) onChanged(); onClose(); }} className="w-9 h-9 flex items-center justify-center rounded hover:bg-background-100 cursor-pointer shrink-0"><i className="ri-close-line text-foreground-500"></i></button>
          </div>
        </div>

        <div className="px-5 border-b border-background-200/70 shrink-0 overflow-x-auto">
          <div className="flex gap-4">{tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`text-xs font-medium py-3 border-b-2 cursor-pointer whitespace-nowrap transition-all ${tab === t.id ? 'border-primary-500 text-primary-600' : 'border-transparent text-foreground-500 hover:text-foreground-700'}`}>{t.label}</button>))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === 'overview' && (
            <div className="space-y-4">
              <div className="bg-background-100 rounded-lg p-3 text-xs">
                <p className="font-medium text-foreground-700 mb-1">注册表</p>
                <Row k="标识" v={device.id} />
                <Row k="类型" v={typeLabels[device.type] ?? device.type} />
                <Row k="状态" v={device.revoked ? '已吊销' : device.status === 'online' ? '在线' : '离线'} />
                <Row k="最后调用 hub" v={timeAgo(device.lastSeen)} />
              </div>
              <div className="bg-background-100 rounded-lg p-3 text-xs">
                <p className="font-medium text-foreground-700 mb-1">私有网</p>
                <Row k="接入" v={text(device.networkType)} />
                <Row k="overlay IP" v={text(device.overlayIP)} />
              </div>
              {device.telemetryAt ? (
                <div className="bg-background-100 rounded-lg p-3 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-foreground-700">遥测</p>
                    <span className="text-[10px] text-foreground-400">上报于 {timeAgo(device.telemetryAt)}</span>
                  </div>
                  <Row k="电量" v={pct(device.battery)} />
                  <Row k="温度" v={degC(device.temperature)} />
                  <Row k="CPU / GPU" v={`${pct(device.cpuUsage)} / ${pct(device.gpuUsage)}`} />
                  <Row k="内存 / 磁盘" v={`${pct(device.memoryUsage)} / ${pct(device.diskUsage)}`} />
                  <Row k="版本" v={text(device.version)} />
                  <Row k="能力楼层" v={device.capabilityLevel ?? NA} />
                  <p className="text-[10px] text-foreground-400 mt-1.5">
                    这次没报的项显示 —— 设备可以只报一部分,hub 不会把没报的当 0。
                  </p>
                </div>
              ) : (
                <div className="bg-background-100 rounded-lg">
                  <NoDataSource title="这台设备还没上报过遥测"
                    reason="电量、温度、CPU/GPU、固件版本由设备主动 POST /api/v1/devices/telemetry 上报 —— 推模型才符合边缘自治(设备可能在 NAT 后、可能断网),hub 不去轮询狗。"
                    phase="设备侧接线后此处自动变真" />
                </div>
              )}
            </div>
          )}

          {tab === 'access' && (
            <div className="space-y-3">
              {error && <p className="text-xs text-red-700 bg-red-50 border border-red-200/70 rounded-lg px-3 py-2">{error}</p>}
              {newToken && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-700 font-semibold mb-2">新令牌只显示这一次,旧令牌已立即失效</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 bg-background-50 border border-background-200/70 rounded px-2 py-2 font-mono text-[11px] break-all">{newToken}</div>
                    <button onClick={() => { navigator.clipboard.writeText(newToken); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="px-3 py-2 bg-primary-500 text-white text-xs rounded cursor-pointer whitespace-nowrap">{copied ? '已复制' : '复制'}</button>
                  </div>
                  <p className="text-[11px] text-red-600/80 mt-2">这台设备在填回新令牌之前会一直 401。</p>
                  {/* 列表刷新压到这个按钮后面 —— 在此之前页面不重绘, 令牌不会被刷掉。 */}
                  <button onClick={tokenTaken} className="mt-2 w-full px-3 py-2 text-xs font-medium border border-red-200 text-red-700 rounded cursor-pointer hover:bg-red-100/60 whitespace-nowrap">我已保存,收起</button>
                </div>
              )}
              <button onClick={() => setConfirm('rotate')} disabled={!!busy} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border border-background-200/70 rounded-lg hover:bg-background-100 cursor-pointer whitespace-nowrap"><i className="ri-refresh-line" />轮换令牌</button>
              <button onClick={() => setConfirm('revoke')} disabled={!!busy || device.revoked} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"><i className="ri-forbid-line" />{device.revoked ? '已吊销' : '吊销令牌'}</button>
              <p className="text-[11px] text-foreground-400 leading-relaxed">轮换/吊销**只影响这一台**设备 —— 这正是每设备一把令牌换掉共用 device_token 的理由。</p>
            </div>
          )}

          {tab === 'config' && <NoDataSource title="配置下发尚未接线" reason="配置要能下发,先要有一条到设备的下行通道(hub→设备)与一份配置版本台账。现在狗上的配置是 ~/0xman/config 里的文件,由 supervisor/cron-extras 读取。" phase="P4 · /ops 配置下发" />}
          {tab === 'logs' && <NoDataSource title="没有远程日志源" reason="日志留在设备本地(journal / logs/)。汇聚到 hub 需要一条日志上报链路和保留策略,尚未建立。" phase="P4 · 远程日志" />}
          {tab === 'upgrade' && <NoDataSource title="OTA 基础设施尚不存在" reason="版本矩阵需要设备上报当前版本,升级需要制品仓库 + 分阶段下发 + 回滚。三样都还没有。" phase="P4 · OTA" />}
        </div>

        {confirm && (
          <div className="absolute inset-0 z-10 flex items-end sm:items-center justify-center p-4" style={{ background: 'oklch(0.12 0.005 90 / 0.4)' }}>
            <div className="bg-background-50 rounded-xl border border-background-200/70 w-full p-5 shadow-lg">
              <h4 className="text-sm font-bold text-foreground-900 mb-1">{confirm === 'rotate' ? '轮换令牌' : '吊销令牌'}</h4>
              <p className="text-xs text-foreground-500 mb-4">
                {confirm === 'rotate'
                  ? `${device.name} 的旧令牌立即失效,需要把新令牌填回设备后它才能重新接入。`
                  : `${device.name} 将立即无法调用 hub。这是一个可审计的动作,不是删除。`}
              </p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setConfirm(null)} disabled={!!busy} className="px-4 py-2 text-xs font-medium text-foreground-600 cursor-pointer whitespace-nowrap">取消</button>
                <button onClick={confirm === 'rotate' ? rotate : revoke} disabled={!!busy} className={`px-4 py-2 rounded-lg text-xs font-medium text-white cursor-pointer whitespace-nowrap ${confirm === 'rotate' ? 'bg-primary-500 hover:bg-primary-600' : 'bg-red-500 hover:bg-red-600'}`}>{busy ? '执行中…' : '确认'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
